import React, { useState, useEffect } from 'react';
import './LoadingStates.css';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BoltIcon from '@mui/icons-material/Bolt';
import BarChartIcon from '@mui/icons-material/BarChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const LoadingStates = ({ phase, query }) => {
  const [currentTip, setCurrentTip] = useState(0);
  const [progress, setProgress] = useState(0);

  // Business insights tips to show during loading
  const loadingTips = [
    "The average Got Tea customer orders 2.3 drinks per visit!",
    "Peak hours are typically 4-5 PM.",
    "Weekend sales are usually 35% higher than weekdays.",
    "Green Matcha and Taro are our top sellers.",
    "Average order processing time is 5-10 minutes.",
    "Personalized recommendations boost sales by 25%."
  ];

  // Phase configurations
  const phaseConfig = {
    processing: {
      icon: <SmartToyIcon fontSize="inherit" />,
      title: 'Understanding your question...',
      duration: 3000,
      progressTarget: 30
    },
    generating: {
      icon: <BoltIcon fontSize="inherit" />,
      title: 'Generating database query...',
      duration: 5000,
      progressTarget: 60
    },
    fetching: {
      icon: <BarChartIcon fontSize="inherit" />,
      title: 'Fetching your data...',
      duration: 17000,
      progressTarget: 90
    },
    formatting: {
      icon: <AutoAwesomeIcon fontSize="inherit" />,
      title: 'Preparing your insights...',
      duration: 5000,
      progressTarget: 100
    }
  };

  const currentPhase = phaseConfig[phase] || phaseConfig.processing;

  // Update progress based on phase
  useEffect(() => {
    const target = currentPhase.progressTarget;
    const duration = currentPhase.duration;
    const increment = target / (duration / 100);
    
    const interval = setInterval(() => {
      setProgress(prev => {
        const next = prev + increment;
        return next >= target ? target : next;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [phase, currentPhase]);

  // Rotate tips during long loading phases
  useEffect(() => {
    if (phase === 'fetching') {
      const tipInterval = setInterval(() => {
        setCurrentTip(prev => (prev + 1) % loadingTips.length);
      }, 4000);

      return () => clearInterval(tipInterval);
    }
  }, [phase, loadingTips.length]);

  // Reset progress when phase changes
  useEffect(() => {
    setProgress(0);
  }, [phase]);

  const getPhaseDescription = () => {
    switch (phase) {
      case 'processing':
        return 'Analyzing your natural language query and determining the best approach...';
      case 'generating':
        return 'Converting your question into optimized database queries...';
      case 'fetching':
        return 'Retrieving data from multiple sources and systems...';
      case 'formatting':
        return 'Organizing results and generating insights for display...';
      default:
        return 'Processing your request...';
    }
  };

  return (
    <div className="loading-states">
      <div className="loading-container">
        {/* Main loading header */}
        <div className="loading-header">
          <div className="loading-icon">
            <span className="phase-icon">{currentPhase.icon}</span>
          </div>
          <div className="loading-content">
            <h3 className="loading-title">{currentPhase.title}</h3>
            <p className="loading-description">{getPhaseDescription()}</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
          <div className="progress-text">
            {Math.round(Math.min(progress, 100))}%
          </div>
        </div>

        {/* Query display */}
        {query && (
          <div className="query-display">
            <div className="query-label">Your question:</div>
            <div className="query-text">"{query}"</div>
          </div>
        )}

        {/* Loading tip (shown during data fetching) */}
        {phase === 'fetching' && (
          <div className="loading-tip">
            <div className="tip-content">
              {loadingTips[currentTip]}
            </div>
          </div>
        )}

        {/* Phase indicators */}
        <div className="phase-indicators">
          {Object.keys(phaseConfig).map((phaseName, index) => (
            <div
              key={phaseName}
              className={`phase-indicator ${
                Object.keys(phaseConfig).indexOf(phase) >= index ? 'completed' : ''
              } ${phase === phaseName ? 'active' : ''}`}
            >
              <div className="indicator-dot" />
              <div className="indicator-label">
                {phaseConfig[phaseName].title.split(' ')[0]}
              </div>
            </div>
          ))}
        </div>

        {/* Animated data particles */}
        <div className="data-particles">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="particle"
              style={{
                animationDelay: `${i * 0.5}s`,
                left: `${10 + i * 15}%`
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LoadingStates; 